<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomCard,
  AtomText,
  SatIconButton,
  AtomButton,
} from "@chatfood/design-system";
import { ref, computed, watch } from "vue";
import { PickupOrderingDetails } from "../pickup-ordering-details";
import { DeliveryOrderingDetails } from "../delivery-ordering-details";
import type { IOrderingDetailsProps } from "./props";
import type { IOrderingDetailsEmits } from "./emits";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useLocale } from "~/composables/use-locale";
import { useDomainLabel } from "~/composables/use-domain-label";

const emits = defineEmits<IOrderingDetailsEmits>();
const props = defineProps<IOrderingDetailsProps>();

const css = bemBuilder("ordering-details");
const { t } = useLocale();
const { orderingModeMap } = useDomainLabel();

const basket = useBasketStore();
const business = useBusinessStore();

const showMenu = ref<boolean>(props.showMenu);
const orderingMode = ref<IDomainOrderingMode>(basket.orderingMode);

const openToken = ref(0);

const isPickupSelected = computed(() => orderingMode.value === "pickup");
const isDeliverySelected = computed(() => orderingMode.value === "delivery");

const toggleShowMenu = () => {
  emits("toggleShowMenu");
};

const onSelectDelivery = () => {
  orderingMode.value = "delivery";
};

const onSelectPickup = () => {
  orderingMode.value = "pickup";
};

watch(
  () => props.showMenu,
  (value) => {
    showMenu.value = value;

    if (value) {
      orderingMode.value = basket.orderingMode;
      openToken.value++;
    }
  },
);
</script>

<template>
  <AtomCard
    padding="none"
    :class="[
      css(''),
      {
        [css('', 'drop-down')]: props.asDropdown,
        [css('show-content')]: showMenu,
      },
    ]"
  >
    <div :class="css('header')">
      <SatIconButton
        icon-name="close"
        variant="secondary"
        theme="tailored"
        size="extra-small"
        :class="css('dismiss')"
        @click="toggleShowMenu"
      />
      <AtomText type="subtitle">
        {{ t("components.ordering_details.title") }}
      </AtomText>
    </div>
    <div
      v-if="business.hasDelivery && business.hasPickup"
      :class="css('mode-switcher')"
    >
      <AtomButton
        :class="css('mode-button')"
        :variant="isDeliverySelected ? 'primary' : 'secondary'"
        :theme="isDeliverySelected ? 'filled' : 'tailored'"
        @click="onSelectDelivery"
      >
        {{ orderingModeMap["delivery"] }}
      </AtomButton>
      <AtomButton
        :class="css('mode-button')"
        :variant="isPickupSelected ? 'primary' : 'secondary'"
        :theme="isPickupSelected ? 'filled' : 'tailored'"
        @click="onSelectPickup"
      >
        {{ orderingModeMap["pickup"] }}
      </AtomButton>
    </div>
    <DeliveryOrderingDetails
      v-if="isDeliverySelected"
      :key="`delivery-${openToken}`"
      @update="toggleShowMenu"
    />
    <PickupOrderingDetails
      v-if="isPickupSelected"
      :key="`pickup-${openToken}`"
      @update="toggleShowMenu"
    />
  </AtomCard>
</template>

<style lang="scss" scoped>
.ordering-details {
  min-width: 320px;
  overflow: auto;
  z-index: 1;
  height: fit-content;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  max-height: 0;
  box-shadow: none;
  transition:
    box-shadow 0.2s ease-in-out,
    max-height 0.3s ease-in-out;

  &--drop-down {
    position: absolute;
    top: 42px;
    right: 0;
    border-bottom-left-radius: var(--radii-card-md);
    border-bottom-right-radius: var(--radii-card-md);
  }

  &__show-content {
    box-shadow: var(--elevation-level-2);
    height: fit-content;
    max-height: 90vh;
    transition:
      box-shadow 400ms ease-in-out,
      max-height 500ms ease-in-out;
  }

  &__confirm-wrapper {
    padding: var(--spacing-lg);
  }

  &__header {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    margin: var(--spacing-lg) var(--spacing-md) 0;
  }

  &__dismiss {
    position: absolute;
    left: 0;
  }

  &__mode-switcher {
    padding: var(--spacing-lg) var(--spacing-md);
    border-bottom: 1px solid var(--color-base-95);
    display: flex;
    gap: 14px;
  }

  &__mode-button {
    flex-grow: 1;
  }
}
</style>
